import { useEffect, useRef, useState } from 'react';

interface Props {
  file: File;
  /** Target output dimensions. The crop window is shown in this aspect ratio. */
  targetWidth: number;
  targetHeight: number;
  /** Title above the picker. */
  title?: string;
  onConfirm(result: { blob: Blob; width: number; height: number }): void;
  onCancel(): void;
}

/**
 * Pan + zoom crop tool. The user drags the image to position it and uses a
 * slider (or wheel) to zoom. The visible crop window is fixed at the target
 * aspect ratio. On confirm we render the visible region to a canvas at the
 * exact target dimensions and emit a PNG blob.
 *
 * Pure-canvas, no library deps. Sized as a centered modal — same UX shape
 * as MagicWandPicker.
 */
export function CropPicker({
  file,
  targetWidth,
  targetHeight,
  title,
  onConfirm,
  onCancel,
}: Props) {
  const [bitmap, setBitmap] = useState<ImageBitmap | null>(null);
  // Pan offset in CSS pixels (relative to crop-window center) + scale.
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const [scale, setScale] = useState(1);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const dragRef = useRef<{ x: number; y: number; tx: number; ty: number } | null>(null);

  const winSize = guessCropWindowSize(targetWidth, targetHeight);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const bm = await createImageBitmap(file);
      if (cancelled) {
        bm.close();
        return;
      }
      setBitmap(bm);
      // Default scale: cover the crop window (so no empty space).
      const sx = winSize.w / bm.width;
      const sy = winSize.h / bm.height;
      setScale(Math.max(sx, sy));
      setTx(0);
      setTy(0);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, targetWidth, targetHeight]);

  /**
   * Draw the source image into a canvas of `outW × outH` at the current
   * pan/zoom. Used both for the live preview (with window-sized output)
   * and for the final blob (with target-sized output) so what you see is
   * literally what you get.
   */
  const renderToCanvas = (out: HTMLCanvasElement, outW: number, outH: number) => {
    if (!bitmap) return;
    const ctx = out.getContext('2d');
    if (!ctx) return;
    out.width = outW;
    out.height = outH;
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, outW, outH);
    // Scale factor from CSS-pixel pan space to output-pixel space.
    const k = outW / winSize.w;
    const drawW = bitmap.width * scale * k;
    const drawH = bitmap.height * scale * k;
    const cx = outW / 2 + tx * k;
    const cy = outH / 2 + ty * k;
    ctx.drawImage(bitmap, cx - drawW / 2, cy - drawH / 2, drawW, drawH);
  };

  // Repaint the live preview canvas whenever pan/zoom/bitmap change.
  useEffect(() => {
    if (!canvasRef.current || !bitmap) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    renderToCanvas(canvasRef.current, winSize.w * dpr, winSize.h * dpr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bitmap, tx, ty, scale, targetWidth, targetHeight]);

  const renderToBlob = async (): Promise<Blob> => {
    if (!bitmap) throw new Error('No bitmap');
    const out = document.createElement('canvas');
    renderToCanvas(out, targetWidth, targetHeight);
    return await new Promise<Blob>((resolve, reject) => {
      out.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('toBlob returned null'))),
        'image/png',
      );
    });
  };

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    (e.target as Element).setPointerCapture?.(e.pointerId);
    dragRef.current = { x: e.clientX, y: e.clientY, tx, ty };
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    if (!d) return;
    setTx(d.tx + (e.clientX - d.x));
    setTy(d.ty + (e.clientY - d.y));
  };
  const onPointerUp = () => {
    dragRef.current = null;
  };

  return (
    <div
      role="dialog"
      aria-modal
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.7)',
        backdropFilter: 'blur(6px)',
        zIndex: 60,
        display: 'grid',
        placeItems: 'center',
        padding: 16,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 560,
          background: 'rgba(11,11,15,0.96)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 16,
          padding: 20,
          display: 'grid',
          gap: 14,
        }}
      >
        <header style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>{title ?? 'Crop & zoom'}</h2>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Close"
            style={{ background: 'transparent', border: 'none', color: 'var(--text-dim)', fontSize: 22, cursor: 'pointer' }}
          >
            ×
          </button>
        </header>
        <p style={{ color: 'var(--text-dim)', margin: 0, fontSize: 13 }}>
          Drag to position. Zoom with the slider. Target: {targetWidth} × {targetHeight}.
        </p>
        <div
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          style={{
            position: 'relative',
            width: winSize.w,
            height: winSize.h,
            margin: '0 auto',
            borderRadius: 8,
            overflow: 'hidden',
            cursor: 'grab',
            touchAction: 'none',
            background: '#000',
          }}
        >
          <canvas
            ref={canvasRef}
            style={{
              width: winSize.w,
              height: winSize.h,
              display: 'block',
              userSelect: 'none',
              pointerEvents: 'none',
            }}
          />
          {/* rule-of-thirds guides */}
          <div style={overlayGuideStyle} />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
          Zoom
          <input
            type="range"
            min={0.1}
            max={4}
            step={0.01}
            value={scale}
            onChange={(e) => setScale(Number(e.target.value))}
            style={{ flex: 1 }}
          />
          <span style={{ width: 40, textAlign: 'right' }}>{scale.toFixed(2)}×</span>
        </label>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '10px 16px',
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.18)',
              color: 'var(--text)',
              borderRadius: 999,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={async () => {
              const blob = await renderToBlob();
              onConfirm({ blob, width: targetWidth, height: targetHeight });
            }}
            style={{
              padding: '10px 20px',
              background: '#ff3a8c',
              color: '#fff',
              border: 'none',
              borderRadius: 999,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Use crop
          </button>
        </div>
      </div>
    </div>
  );
}

/** Pick a CSS crop window that fits the dialog and matches the target aspect. */
function guessCropWindowSize(targetW: number, targetH: number): { w: number; h: number } {
  const ar = targetW / targetH;
  const maxW = 360;
  const maxH = 420;
  // Fit by either width or height, whichever is more constraining.
  let w = maxW;
  let h = maxW / ar;
  if (h > maxH) {
    h = maxH;
    w = maxH * ar;
  }
  return { w: Math.round(w), h: Math.round(h) };
}

const overlayGuideStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  pointerEvents: 'none',
  background:
    'linear-gradient(0deg, transparent calc(33.33% - 1px), rgba(255,255,255,0.15) 33.33%, rgba(255,255,255,0.15) calc(33.33% + 1px), transparent calc(33.33% + 1px), transparent calc(66.66% - 1px), rgba(255,255,255,0.15) 66.66%, rgba(255,255,255,0.15) calc(66.66% + 1px), transparent calc(66.66% + 1px)),' +
    'linear-gradient(90deg, transparent calc(33.33% - 1px), rgba(255,255,255,0.15) 33.33%, rgba(255,255,255,0.15) calc(33.33% + 1px), transparent calc(33.33% + 1px), transparent calc(66.66% - 1px), rgba(255,255,255,0.15) 66.66%, rgba(255,255,255,0.15) calc(66.66% + 1px), transparent calc(66.66% + 1px))',
};
